import { Component, Inject, inject, OnInit } from '@angular/core';
import { AdvancedSearchComponent } from '@lib-ui/advanced-search/ui/advanced-search.component';
import { CommonTableComponent } from '@lib-ui/common-table/ui/common-table.component';
import { LoaderComponent } from '@lib-ui/loader/loader.component';
import { FilterConfig } from '@lib-ui/advanced-search/models/advanced-search.interface';
import { EnumService } from '@lib-services/enum.service';
import { ReportEnum } from '@libs-enums/report.enum';
import { GroupsSelectorService } from '@lib-services/groups-selector.service';
import { FormControl } from '@angular/forms';
import { SessionStorageService } from '@lib-services/session-storage.service';
import { MatButton } from '@angular/material/button';
import { TIME_ZONE_EN, TimeZoneEnum } from '@libs-enums/time-zone.enum';
import { ReportService } from '@lib-services/report.service';
import { DOCUMENT } from '@angular/common';
import { MessageService } from '@lib-services/message.service';
import { ReportQueryService } from './report-query.service';
import { TimeZoneService } from '@lib-services/time-zone.service';
import { Params } from '@angular/router';
import { StatusSelectorService } from '@lib-services/status-selector.service';
import { AllGroupsSelectorService } from '@lib-services/all-groups-selector.service';

@Component({
    selector: 'app-report',
    standalone: true,
    imports: [
        AdvancedSearchComponent,
        CommonTableComponent,
        LoaderComponent,
        MatButton,
    ],
    templateUrl: './report.component.html',
    styleUrl: './report.component.scss',
    providers: [ReportQueryService]
})
export class ReportComponent implements OnInit {

    filterData: Record<string, string | null> = {
        report: null,
        group: null,
        status: null,
        dateFrom: null,
    };

    timeZoneService = inject(TimeZoneService);
    sessionStorageService = inject(SessionStorageService);
    enumService = inject(EnumService);
    reportService = inject(ReportService);
    messageService = inject(MessageService);
    reportQueryService = inject(ReportQueryService);
    groupCtrl = new FormControl();
    statusCtrl = new FormControl();

    filterConfig: FilterConfig[] = [
        {
            type: 'dropdown',
            label: 'Вид отчета',
            name: 'report',
            options: this.enumService.enumTranslateReportTypeToArrSelects(ReportEnum),
        },
        {
            type: 'common-dropdown',
            label: 'Группа',
            name: 'group',
            commonDropdownConfig: {
                service: inject(AllGroupsSelectorService),
                formCtrl: this.groupCtrl,
                multiple: true,
            },
        },
        {
            type: 'common-dropdown',
            label: 'Статус заявки',
            name: 'status',
            commonDropdownConfig: {
                service: inject(StatusSelectorService),
                formCtrl: this.statusCtrl,
                multiple: true,
            },
        },
        {
            type: 'dateRangePicker',
            label: 'Период',
            name: 'dateFrom',
        },
    ];

    constructor(
        @Inject(DOCUMENT) private document: Document,
    ) {}

    ngOnInit(): void {
        if (this.sessionStorageService.getItem('report-filters')) {
            this.sessionStorageService.removeItem('report-filters');
        }
    }

    applyFilters(filterData: Record<string, string | null>): void {
        for (const key in filterData) {
            if (Object.prototype.hasOwnProperty.call(filterData, key)) { // проверка, что свойство принадлежит самому объекту
                if (key === 'report') {
                    // TODO выстроить логику если будет отдельный api
                    delete this.filterData['report'];
                    continue;
                }

                if (key === 'group') {
                    const newGroups = filterData[key]?.split(',');
                    const groups = this.filterData[key]?.split(',') || [];

                    if (!filterData[key]) {
                        this.filterData[key] = filterData[key];
                        continue;
                    }

                    if (newGroups && newGroups.length === 1) {
                        if (groups.includes(newGroups[0])) {
                            const idx = groups.indexOf(newGroups[0]);
                            groups.splice(idx, 1);
                        } else {
                            groups.push(newGroups[0]);
                        }
                    } else if (newGroups && newGroups.length > 1) {
                        newGroups.forEach(newGroup => {
                            if (!groups.includes(newGroup)) {
                                groups.push(newGroup);
                            }
                        });
                    }
                    this.filterData[key] = groups.filter(groupId => !!groupId).join();
                } else {
                    this.filterData[key] = filterData[key];
                }
            }
        }
    }

    downloadReport(): void {
        const query: Params | null = this.reportQueryService.createSearchParams(this.filterData);
        if(!query) {
            this.messageService.errorMessage('Необходимо выбрать фильтры');
            return;
        }

        let hasAllFilters = true;
        const hasNull = Object.values(this.filterData).some(value => {
            return value === null;
        });

        for (const key in query) {
            if (Object.prototype.hasOwnProperty.call(query, key)) {
                const value = query[key];

                // Проверяем, является ли значение массивом
                if (Array.isArray(value)) {
                    // Если массив пустой или содержит только пустые строки
                    if (value.length === 0 || value.every(item => item === '')) {
                        hasAllFilters = false;
                        break;
                    }
                }
                // Проверяем обычное строковое значение
                else if (!value) {
                    hasAllFilters = false;
                    break;
                }
            }
        }

        if (!hasAllFilters || hasNull) {
            this.messageService.errorMessage('Указаны не все фильтры');
            return;
        }

        this.reportService.saveReport({
            ...query,
            timeZone: TIME_ZONE_EN[this.timeZoneService.currentTimeZone() as TimeZoneEnum]
        }).subscribe({
            next: (blob: Blob) => {
                // Создаем ссылку для скачивания файла
                const url = URL.createObjectURL(blob);
                const a = this.document.createElement('a');
                a.href = url;
                a.download = `executor_load_report_${new Date().getTime()}.csv`;
                this.document.body.appendChild(a);
                a.click();

                // Очистка
                URL.revokeObjectURL(url);
                a.remove();
            },
            error: () => this.messageService.errorMessage('Ошибка выгрузки отчета')
        });
    }

}
